// React.
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useReturnTo } from '../../../hooks/useReturnTo';

// Third-party packages.
import { Dayjs } from 'dayjs';
import * as dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Types.
import Action from '../../../types/Action';
import Filter from '../../../types/Filter';

// Constants.
import { ACTIONS } from '../../../component/Constants';

// Context.
import { FilterContext } from '../../../context/FilterContext';
import { useUserRole } from '../../../context/UserRoleContext';

// Components.
import Alert from '../../../component/Alert';

// Form Components
import {
    TextInput,
    CheckboxField
} from '../../../components/forms';

// Helpers.
import { fetchFilters, getParameter, getTimeZone, sendData }
    from '../../../helpers/utils';
import { showError, getErrorMessage } from '../../../utils/showAlert';

// Local constants.
const DATE_FORMAT = 'YYYY-MM-DD';

// Local functions.
function formatDateTime(date: Dayjs, time: string): string {
    return date.format(DATE_FORMAT) + '+' + time;
}

function makeParameters(fd: Dayjs, ft: string, td: Dayjs, tt: string): string {
    return '?from=' + formatDateTime(fd, ft) +
            '&to=' + formatDateTime(td, tt) +
            '&tz=' + getTimeZone();
}

function extendTimeString(value: string): string {
    const extendedValue = '0000-01-01 00:00:00';

    if (value.length < extendedValue.length) {
        value += extendedValue.substring(value.length);
    }

    return value;
}

function getDate(date: string | null): Dayjs {
    return date ? dayjs.default(date) : dayjs.default();
}

function getTime(date: string | null): string {
    return date ? extendTimeString(date).substring(11) : '00:00:00';
}

export default function Detail() {
    const { filters, setFilters } = useContext(FilterContext);
    const { isAdmin } = useUserRole();
    const { actionId, filterId } = useParams();
    const returnFromForm = useReturnTo('/admin/filters');

    // Set the state.
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const [isMissing, setIsMissing] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [disabled, setDisabled] = useState<boolean>
    (actionId === ACTIONS.DELETE.type);
    const [id, setId] = useState<number>(0);
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [fromDate, setFromDate] = useState<Dayjs>(dayjs.default().subtract(1, 'day'));
    const [fromTime, setFromTime] = useState<string>('00:00:00');
    const [toDate, setToDate] = useState<Dayjs>(dayjs.default());
    const [toTime, setToTime] = useState<string>('00:00:00');
    const [keepData, setKeepData] = useState<boolean>(false);
    const [advanced, setAdvanced] = useState<boolean>(false);
    const [parameters, setParameters] = useState<string>
    (makeParameters(fromDate, fromTime, toDate, toTime));
    const [nameUsed, setNameUsed] = useState<boolean>(false);
    const [nameClass, setNameClass] = useState<string>('');
    const [inputClass, setInputClass] = useState<string>('');
    const [parameterClass, setParameterClass] = useState<string>('');

    useEffect(() => {
        const getFilter = (filterId: number) => {
            const matchedFilters = filters.filter((object: Filter) =>
                    object.id === filterId);

            if (matchedFilters.length === 1) {
                const f: Filter = matchedFilters[0];
                const from: string | null = getParameter(f.parameters, 'from');
                const to: string | null = getParameter(f.parameters, 'to');
                const fromDate: Dayjs = getDate(from);
                const fromTime: string = getTime(from);
                const toDate: Dayjs = getDate(to);
                const toTime: string = getTime(to);
                setId(f.id);
                setName(f.name);
                setDescription(f.description || '');
                setFromDate(fromDate);
                setFromTime(fromTime);
                setToDate(toDate);
                setToTime(toTime);
                setKeepData(f.keepdata);
                setAdvanced(f.parameters !== makeParameters(fromDate,
                        fromTime, toDate, toTime));
                setParameters(f.parameters);
            } else {
                setIsMissing(true);
            }
        }

        const getAction = () => {
            switch (actionId) {
                case 'create':
                    setAction(ACTIONS.CREATE);
                    break;

                case 'edit':
                    setAction(ACTIONS.UPDATE);
                    break;

                case 'delete':
                    setAction(ACTIONS.DELETE);
                    setDisabled(true);
                    break;

                default:
                    console.error('Unknown action requested', actionId);
                    break;
            }
        }

        getAction();

        if (filterId) {
            getFilter(Number(filterId));
        }

        
    }, [filters, actionId, filterId]);

    function isDuplicateName(value: string): boolean {
        return filters.findIndex((object: Filter) => object.id !== id &&
                object.name.toLowerCase() === value.toLowerCase()) >= 0;
    }

    function handleNameBlur() {
        // Trim any white space.
        const value = name.trim();

        // Save to state.
        setName(value);

        if (value === '') {
            setNameClass('is-invalid');
            setNameUsed(false);
        } else if (isDuplicateName(value)) {
            setNameClass('is-invalid');
            setNameUsed(true);
        } else {
            setNameClass('is-valid');
            setNameUsed(false);
        }
    }

    function handleParameterBlur() {
        try {
            const params = new URLSearchParams(parameters);
            const fromParam = params.get('from');
            const toParam = params.get('to');

            if (!fromParam || !toParam) {
                setParameterClass('is-invalid');
                return;
            }

            const from = dayjs.default(fromParam.replace(' ', 'T'));
            const to = dayjs.default(toParam.replace(' ', 'T'));

            if (!from.isValid() || !to.isValid() || !from.isBefore(to)) {
                setParameterClass('is-invalid');
                return;
            }

            const requiredFilters = ['from', 'to', 'tz'];
            for (const key of requiredFilters) {
                if (!params.has(key)) {
                    setParameterClass('is-invalid');
                    return;
                }
            }

            const knownFilters = ['from', 'to', 'tz', 'waits', 'databases', 'sessions', 'clients', 'users', 'programs', 'modules', 'waitevent', 'session', 'username', 'program'];
            for (const [key] of Array.from(params.entries())) {
                const cleanKey = key.toLowerCase().split('?')[0];
                if (!knownFilters.includes(cleanKey)) {
                    console.warn(`Unknown filter detected: ${cleanKey}`);
                }
            }

            setParameterClass('is-valid');
        } catch (e) {
            setParameterClass('is-invalid');
        }
    }



    function isValidTimeFormat(time: string): boolean {
        return /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(:[0-5][0-9])?$/.test(time);
    }

    function combineDateTime(date: Dayjs, time: string): Dayjs {
        const [h, m, s] = time.split(':').map((t) => parseInt(t, 10));
        return date.hour(h).minute(m).second(s || 0);
    }

    function validateDateTimeRange(fromD: Dayjs, fromT: string, toD: Dayjs, toT: string): boolean {
        if (!fromD.isValid() || !toD.isValid()) return false;
        if (!isValidTimeFormat(fromT) || !isValidTimeFormat(toT)) return false;

        const fromDateTime = combineDateTime(fromD, fromT);
        const toDateTime = combineDateTime(toD, toT);

        return fromDateTime.isBefore(toDateTime);
    }

    function updateParametersAndValidate(newFromDate = fromDate, newFromTime = fromTime, newToDate = toDate, newToTime = toTime) {
        if (!validateDateTimeRange(newFromDate, newFromTime, newToDate, newToTime)) {
            setInputClass('is-invalid');
            setParameterClass('is-invalid');
            return;
        }

        setParameters(makeParameters(newFromDate, newFromTime, newToDate, newToTime));
        setInputClass('is-valid');
        setParameterClass('is-valid');
    }

    function changeFromDate(date: Date | null) {
        if (date === null) {
            setInputClass('is-invalid');
            return;
        }

        const value = dayjs.default(date);
        setFromDate(value);
        setAdvanced(false);
        updateParametersAndValidate(value, fromTime, toDate, toTime);
    }

    function changeToDate(date: Date | null) {
        if (date === null) {
            setInputClass('is-invalid');
            return;
        }

        const value = dayjs.default(date);
        setToDate(value);
        setAdvanced(false);
        updateParametersAndValidate(fromDate, fromTime, value, toTime);
    }

    function changeFromTime(value: string) {
        setFromTime(value);
        setAdvanced(false);
        updateParametersAndValidate(fromDate, value, toDate, toTime);
    }

    function changeToTime(value: string) {
        setToTime(value);
        setAdvanced(false);
        updateParametersAndValidate(fromDate, fromTime, toDate, value);
    }

    function validateTimeInputs() {
        updateParametersAndValidate();
    }

    function isValidForm(): boolean {
        return nameClass !== 'is-invalid' &&
                inputClass !== 'is-invalid' &&
                parameterClass !== 'is-invalid';
    }

    async function saveFilterAndRefreshList(method: string, data: any) {
        try {
            await sendData(method, '/filter', data);
            setFilters(await fetchFilters());
            returnFromForm()
        } catch (x: any) {
            console.error('Failed to complete requested action', action, x);
            const errorMessage = getErrorMessage(x);
            const actionName = action === ACTIONS.CREATE ? 'create' : action === ACTIONS.UPDATE ? 'update' : 'delete';
            showError(`Failed to ${actionName} snapshot`, errorMessage);
        }
    }

    async function saveFilter() {
        // Validate the form.
        if (!isValidForm()) {
            setIsValid(false);
            return;
        }

        setIsValid(true);

        if (!isAdmin && action !== ACTIONS.DELETE) {
            const from = getParameter(parameters, 'from');
            const to = getParameter(parameters, 'to');
            const hours = from && to
                ? dayjs.default(extendTimeString(to)).diff(dayjs.default(extendTimeString(from)), 'hour', true)
                : NaN;

            if (!(hours <= 24)) {
                showError('Failed to save snapshot', 'Snapshots are limited to one day.');
                return;
            }
        }

        const data =
                [{
                    id,
                    name,
                    parameters,
                    keepdata: keepData,
                    description
                }];

        switch (action) {
            case ACTIONS.CREATE:
                await saveFilterAndRefreshList('POST', data);
                break;

            case ACTIONS.UPDATE:
                await saveFilterAndRefreshList('PUT', data);
                break;

            case ACTIONS.DELETE:
                await saveFilterAndRefreshList('DELETE', data);
                break;

            default:
                // Unknown action requested.
                console.error('Unknown action requested', actionId);
                break;
        }
    }

    return (
            <div className="col">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-camera-alt fa-fw mr-10" aria-hidden="true"></i>
                        {action.title} Snapshot
                    </div>
                    <div className="card-body">
                        {isMissing ? (
                                <React.Fragment>
                                    <Alert message="The requested snapshot is either invalid or no longer available." heading="Invalid request" variant="alert-danger"/>
                                    <div className="row g-0 form">
                                        <div className="col-xs-12 col-md-9 col-lg-6 actions">
                                            <Link to="/admin/filters" role="button" className="btn btn-secondary"><i className="fal fa-arrow-circle-left fa-fw fa-sm"></i><span>Back</span></Link>
                                        </div>
                                    </div>
                                </React.Fragment>
                        ) : (
                                <React.Fragment>
                                    {action.type === 'delete' && (
                                            <Alert message="Please check and ensure you want to delete this snapshot." heading="Deletes are final and cannot be undone" variant="alert-warning"/>
                                    )}

                                    {!isValid && (
                                            <Alert message="Please complete all the required fields below." heading="Missing required fields" variant="alert-danger"/>
                                    )}

                                    <p>Please complete the required details below:</p>

                                    <div className="row g-0 form">

                                        {/* Name */}
                                        <TextInput
                                            id="name"
                                            label="Name"
                                            value={name}
                                            onChange={(value) => setName(value)}
                                            onBlur={handleNameBlur}
                                            required
                                            disabled={disabled}
                                            maxLength={250}
                                            tooltip="The snapshot name"
                                            error={nameUsed ? "Snapshot name already used." : undefined}
                                            className={nameClass}
                                            autoFocus
                                            dataLpIgnore
                                        />

                                        {/* Description */}
                                        <TextInput
                                            id="description"
                                            label="Description"
                                            value={description}
                                            onChange={(value) => setDescription(value)}
                                            disabled={disabled}
                                            maxLength={250}
                                            tooltip="Description"
                                            dataLpIgnore
                                        />

                                        {/* From Date */}
                                        <div className="col-xs-12 col-md-3">
                                            <span className="required"><label htmlFor="fromDate" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="From date">From Date</label></span>
                                        </div>
                                        <div className="col-xs-12 col-md-6 col-lg-3 ">
                                            <DatePicker
                                                    id="fromDate"
                                                    className={`form-control ${inputClass}`}
                                                    dateFormat="yyyy-MM-dd"
                                                    placeholderText="yyyy-MM-dd"
                                                    popperPlacement="top-end"
                                                    selected={fromDate.toDate()}
                                                    onChange={(date: Date) => changeFromDate(date)}
                                                    shouldCloseOnSelect={true}
                                                    disabled={disabled || advanced}
                                                    data-lpignore={true}
                                            />
                                        </div>
                                        <div className="w-100"></div>

                                        {/* From Time */}
                                        <TextInput
                                            id="fromTime"
                                            label="From Time"
                                            value={fromTime}
                                            onChange={(value) => changeFromTime(value)}
                                            onBlur={validateTimeInputs}
                                            required
                                            disabled={disabled || advanced}
                                            maxLength={8}
                                            placeholder="HH:MM:SS"
                                            tooltip="From time"
                                            className={inputClass}
                                            dataLpIgnore
                                        />

                                        {/* To Date */}
                                        <div className="col-xs-12 col-md-3">
                                            <span className="required"><label htmlFor="toDate" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="To date">To Date</label></span>
                                        </div>
                                        <div className="col-xs-12 col-md-6 col-lg-3 ">
                                            <DatePicker
                                                    id="toDate"
                                                    className={`form-control ${inputClass}`}
                                                    dateFormat="yyyy-MM-dd"
                                                    placeholderText="yyyy-MM-dd"
                                                    popperPlacement="top-end"
                                                    selected={toDate.toDate()}
                                                    onChange={(date: Date) => changeToDate(date)}
                                                    shouldCloseOnSelect={true}
                                                    disabled={disabled || advanced}
                                                    data-lpignore={true}
                                            />
                                        </div>
                                        <div className="w-100"></div>

                                        {/* To Time */}
                                        <TextInput
                                            id="toTime"
                                            label="To Time"
                                            value={toTime}
                                            onChange={(value) => changeToTime(value)}
                                            onBlur={validateTimeInputs}
                                            required
                                            disabled={disabled || advanced}
                                            maxLength={8}
                                            placeholder="HH:MM:SS"
                                            tooltip="To time"
                                            className={inputClass}
                                            dataLpIgnore
                                        />

                                        {/* Keep Data */}
                                        <CheckboxField
                                            id="keepdata"
                                            label="Keep Data"
                                            checked={keepData}
                                            onChange={(checked) => setKeepData(checked)}
                                            disabled={disabled}
                                            tooltip="Keep data"
                                        />

                                        {/* Advanced Mode */}
                                        <CheckboxField
                                            id="advanced"
                                            label="Advanced Mode"
                                            checked={advanced}
                                            onChange={(checked) => setAdvanced(checked)}
                                            disabled={disabled}
                                            tooltip="Enable advanced mode"
                                        />

                                        {/* Parameters */}
                                        <TextInput
                                            id="parameters"
                                            label="Parameters"
                                            value={parameters}
                                            onChange={(value) => setParameters(value)}
                                            onBlur={handleParameterBlur}
                                            required
                                            disabled={disabled || !advanced}
                                            maxLength={250}
                                            tooltip="snapshot parameters"
                                            className={parameterClass}
                                            dataLpIgnore
                                        />

                                        {/* Actions */}
                                        <div className="col-xs-12 col-md-12 col-lg-12 actions">
                                            <button className="btn btn-secondary"  onClick={returnFromForm}><i className="fal fa-times-circle fa-fw fa-sm"></i><span>Cancel</span></button>
                                            <button className={`btn btn-${action.variant}`} onClick={saveFilter}><i className={`${action.icon} fa-fw fa-sm`}></i><span>{action.title}</span></button>
                                        </div>
                                    </div>
                                </React.Fragment>
                        )}
                    </div>
                </div>
            </div>
    )
}
